import { useCallback, useMemo } from 'react';
import { BaseAnalyticsFilter } from '@repo/shared/dtos/analytics/AnalyticsFilterDto';
import { SessionTrendDataDto } from '@repo/shared/dtos/analytics/AnalyticsResponseDto';
import { DrillDownModal } from '../shared/DrillDownModal';
import { SessionTrendsChart as PercentageChangeChart } from './percentageChangeChart';
import { Calendar, BarChart3, List } from 'lucide-react';
import { SessionTrendsTable } from './SessionTrendsTable';
import { dateUtils } from '../utils/dateUtils';
import { ControlOption } from '../shared/types';

interface SessionTrendsModalProps {
  data: SessionTrendDataDto[];
  isOpen: boolean;
  onClose: () => void;
  filters?: BaseAnalyticsFilter;
  onTrendClick?: (trend: SessionTrendDataDto) => void;
}

export function SessionTrendsModal({
  data,
  isOpen,
  onClose,
  filters,
  onTrendClick,
}: SessionTrendsModalProps) {
  const exportDataTransform = useCallback(
    (data: SessionTrendDataDto[]) =>
      data.map((trend, index) => ({
        rank: index + 1,
        month: dateUtils.formatFullMonth(trend.monthStart),
        monthStart: trend.monthStart,
        sessionCount: trend.sessionCount,
        totalMinutes: Math.round(trend.totalMinutes),
        averageDuration:
          trend.sessionCount > 0 ? Math.round(trend.totalMinutes / trend.sessionCount) : 0,
        percentageChange: trend.percentageChange,
        changeDirection:
          trend.percentageChange === null
            ? 'No change'
            : trend.percentageChange > 0
              ? 'Increase'
              : 'Decrease',
      })),
    [],
  );

  const viewOptions = useMemo<ControlOption<'chart' | 'table'>[]>(
    () => [
      { value: 'chart', label: 'Chart', icon: <BarChart3 className="h-4 w-4" /> },
      { value: 'table', label: 'Table', icon: <List className="h-4 w-4" /> },
    ],
    [],
  );

  return (
    <DrillDownModal
      title="Session Trends"
      data={data}
      isOpen={isOpen}
      onClose={onClose}
      filters={filters}
      onItemClick={onTrendClick}
      exportDataTransform={exportDataTransform}
      exportFileName="session-trends"
      icon={<Calendar className="h-6 w-6" />}
      viewOptions={viewOptions}
    >
      {({ data, activeView, showMetric, onItemClick }) =>
        activeView === 'chart' ? (
          <PercentageChangeChart data={data} showMetric={showMetric} isModalOpen={isOpen} />
        ) : (
          <SessionTrendsTable data={data} onItemClick={onItemClick} />
        )
      }
    </DrillDownModal>
  );
}
